import React from "react";
import {
  FiEdit2,
  FiEye,
  FiMail,
  FiPhone,
  FiToggleLeft,
  FiToggleRight,
  FiTrash2,
} from "react-icons/fi";
import { HiOfficeBuilding, HiUserGroup } from "react-icons/hi";

import AdminButton from "../../_shared/ui/AdminButton";
import { RoleBadge, VisibleBadge } from "./TrombinoStatusBadges";
import { toAbsolutePhotoUrl } from "../../../../services/trombinosServices";

import {
  formatDate,
  formatDateTime,
  getFullName,
  truncateText,
} from "../utils/trombinosAdminUtils";

const FALLBACK_PHOTO = "/img/no-image.jpg";

export default function TrombinoCard({
  item,
  actionId,
  onPreview,
  onEdit,
  onToggleVisible,
  onToggleElected,
  onAskDelete,
}) {
  const fullName = getFullName(item) || "Profil sans nom";
  const photo = item.photo_url
    ? toAbsolutePhotoUrl(item.photo_url)
    : FALLBACK_PHOTO;

  const visibleLoading = actionId === `visible-${item.id}`;
  const electedLoading = actionId === `elected-${item.id}`;

  return (
    <article className="group overflow-hidden rounded-[1.7rem] border border-slate-200 bg-slate-50 transition hover:-translate-y-0.5 hover:border-sky-200 hover:bg-white hover:shadow-xl hover:shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-500/30 dark:hover:bg-slate-900 dark:hover:shadow-black/20">
      <div className="relative overflow-hidden bg-gradient-to-br from-indigo-700 via-sky-600 to-cyan-600 p-5 text-white">
        <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-white/15 blur-2xl" />

        <div className="relative flex items-start justify-between gap-4">
          <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-[1.6rem] border-4 border-white/20 bg-white/15 shadow-lg">
            <img
              src={photo}
              alt={fullName}
              className="h-full w-full object-cover"
              onError={(event) => {
                event.currentTarget.src = FALLBACK_PHOTO;
              }}
            />
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <RoleBadge isElected={!!item.is_elected} />
            <VisibleBadge visible={!!item.is_visible} />
          </div>
        </div>

        <div className="relative mt-5">
          <h3 className="line-clamp-2 text-2xl font-bold leading-snug">
            {fullName}
          </h3>

          {item.fonction ? (
            <p className="mt-2 line-clamp-2 text-sm leading-6 text-white/85">
              {item.fonction}
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col p-4 sm:p-5">
        <div className="flex flex-wrap gap-2">
          {item.groupe ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              <HiUserGroup />
              {item.groupe}
            </span>
          ) : null}

          {item.service?.nom_service ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              <HiOfficeBuilding />
              {item.service.nom_service}
            </span>
          ) : null}
        </div>

        <p className="mt-4 line-clamp-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
          {truncateText(item.presentation_courte || item.bio_html, 210) ||
            "Aucune présentation renseignée."}
        </p>

        <div className="mt-4 flex flex-wrap gap-2 text-xs font-bold text-slate-500 dark:text-slate-400">
          {item.mail_pro ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 dark:bg-slate-800">
              <FiMail />
              {item.mail_pro}
            </span>
          ) : null}

          {item.tel_pro ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 dark:bg-slate-800">
              <FiPhone />
              {item.tel_pro}
            </span>
          ) : null}
        </div>

        <div className="mt-4 flex flex-wrap gap-3 text-xs font-bold text-slate-400 dark:text-slate-500">
          {item.mandat_debut ? (
            <span>
              Mandat : {formatDate(item.mandat_debut)}
              {item.mandat_fin ? ` → ${formatDate(item.mandat_fin)}` : ""}
            </span>
          ) : null}

          <span>Ordre : {item.ordre_affichage ?? 0}</span>
          <span>Créé le {formatDateTime(item.created_at)}</span>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <AdminButton
            icon={FiEye}
            variant="secondary"
            size="sm"
            onClick={() => onPreview(item.id)}
          >
            Voir
          </AdminButton>

          <AdminButton
            icon={FiEdit2}
            variant="soft"
            size="sm"
            onClick={() => onEdit(item.id)}
          >
            Modifier
          </AdminButton>

          <AdminButton
            icon={item.is_visible ? FiToggleLeft : FiToggleRight}
            variant={item.is_visible ? "secondary" : "primary"}
            size="sm"
            loading={visibleLoading}
            onClick={() => onToggleVisible(item.id)}
          >
            {item.is_visible ? "Masquer" : "Afficher"}
          </AdminButton>

          <AdminButton
            icon={FiToggleRight}
            variant="secondary"
            size="sm"
            loading={electedLoading}
            onClick={() => onToggleElected(item.id)}
          >
            {item.is_elected ? "Personnel" : "Élu"}
          </AdminButton>

          <AdminButton
            icon={FiTrash2}
            variant="dangerSoft"
            size="sm"
            onClick={() => onAskDelete(item.id)}
          >
            Supprimer
          </AdminButton>
        </div>
      </div>
    </article>
  );
}
